import type { SVGProps } from 'react'

// HALO glyph family (DESIGN.md, Icons and identity): 20px grid, 1.5px stroke,
// square ends, softened joins, one foreground color. Drawn in
// docs/design/halo-core-preview.html. Lucide covers everything else, restroked
// to match in app/globals.css.

type GlyphProps = SVGProps<SVGSVGElement>

function Glyph({ children, ...props }: GlyphProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="square"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  )
}

/** The suite mark: an open ellipse with a single break at the upper right. */
export function HaloMark(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M13.38 4.56A8 6 0 1 0 17.25 7.46" />
    </Glyph>
  )
}

/** Core: two offset vertical planes around a shared center. */
export function CoreGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M4.75 3.75h6v10h-6zM9.25 6.25h6v10h-6z" />
    </Glyph>
  )
}

/** Pulse: a short signal step inside an incomplete arc. */
export function PulseGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M15.95 13.7A7 7 0 1 0 13.7 15.95" />
      <path d="M6 11h2.5V8h3v4.5H14" />
    </Glyph>
  )
}

/** Assessment: an open square aperture with one measured tick. */
export function AssessmentGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M12.25 3.75h-8.5v12.5h12.5v-8.5" />
      <path d="M3.75 10H7" />
    </Glyph>
  )
}

/**
 * Status symbols: each state has its own shape as well as its own color, so
 * meaning survives without color (DESIGN.md, Tags and labels).
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'live'

const STATUS_CLASS: Record<StatusTone, string> = {
  success: 'fill-current text-halo-success',
  warning: 'fill-current text-halo-warning',
  danger: 'fill-current text-halo-danger',
  info: 'fill-current text-halo-info',
  neutral: 'fill-none stroke-current stroke-[1.5] text-halo-muted',
  live: 'fill-halo-accent stroke-halo-accent-ink stroke-[1.5]',
}

export function StatusGlyph({ tone, className = '' }: { tone: StatusTone; className?: string }) {
  return (
    <svg viewBox="0 0 8 8" aria-hidden className={`size-2 shrink-0 ${STATUS_CLASS[tone]} ${className}`}>
      {tone === 'warning' ? (
        <path d="M4 .5 7.75 7.5H.25z" />
      ) : tone === 'danger' ? (
        <path d="M.75.75h6.5v6.5H.75z" />
      ) : tone === 'info' ? (
        <path d="M4 0 8 4 4 8 0 4z" />
      ) : tone === 'success' ? (
        <circle cx="4" cy="4" r="3.5" />
      ) : (
        <circle cx="4" cy="4" r="3" />
      )}
    </svg>
  )
}

/**
 * HALO Core's logo. The artwork (public/halo-core-mark.png) is an alpha mask:
 * its shading lives in transparency, and the color comes from `currentColor`,
 * so the mark takes the theme's ink like the rest of the type.
 */
export function CoreLogo({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 bg-current ${className}`}
      style={{
        maskImage: 'url(/halo-core-mark.png)',
        WebkitMaskImage: 'url(/halo-core-mark.png)',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  )
}
